"use client";

import { useEffect } from "react";

/** Scroll movement, in px, needed before the header toggles. Filters jitter. */
const DELTA_THRESHOLD = 5;

/**
 * Upper bound for how long a toggle is allowed to keep changing layout. Matches
 * the header's 300ms CSS transition plus a frame; `transitionend` normally ends
 * the window earlier (immediately under `prefers-reduced-motion`).
 */
const SETTLE_TIMEOUT_MS = 350;

type AutoHidingHeaderOptions = {
  ref: React.RefObject<HTMLElement | null>;
  enabled: boolean;
  /** How far the header slides up when hidden — the nav row height. */
  hiddenOffset: number;
};

type HeaderState = {
  el: HTMLElement;
  hiddenOffset: number;
  headerHeight: number;
  lastScrollY: number;
  settledAfter: number;
  hidden: boolean;
};

// The negative marginBottom shrinks document flow by the same amount the
// nav row moves up. Without it, layout keeps reserving the full header
// height and a strip of empty space shows between the search bar and the
// content. Both properties animate together via the header's transition.
function setHidden(state: HeaderState, next: boolean) {
  state.hidden = next;
  state.el.style.transform = next ? `translateY(-${state.hiddenOffset}px)` : "";
  state.el.style.marginBottom = next ? `-${state.hiddenOffset}px` : "";
  state.settledAfter = performance.now() + SETTLE_TIMEOUT_MS;
}

function reveal(state: HeaderState) {
  if (state.hidden) setHidden(state, false);
}

// Hiding shortens the document by hiddenOffset. Near the end of the page
// there is nothing left to absorb that, so the browser clamps the scroll
// offset and the whole page jumps.
function hasRoomToHide(hiddenOffset: number) {
  return (
    document.documentElement.scrollHeight -
      (window.scrollY + window.innerHeight) >=
    hiddenOffset
  );
}

function updateHeader(state: HeaderState) {
  const scrollY = window.scrollY;
  const delta = scrollY - state.lastScrollY;
  state.lastScrollY = scrollY;

  // Collapsing the nav row pulls the scroll offset up by at most
  // hiddenOffset, and a hide never starts closer to the fold than that
  // (below), so this far up the page the offset can only come from real
  // scrolling. Revealing here needs no settled layout.
  if (scrollY <= Math.max(0, state.headerHeight - state.hiddenOffset)) {
    reveal(state);
    return;
  }

  if (performance.now() < state.settledAfter) return;

  // Still above the fold, where the full header belongs on screen — but
  // only once layout has settled, since a collapse in flight moves the
  // offset through this band and would otherwise undo itself.
  if (scrollY <= state.headerHeight) {
    reveal(state);
    return;
  }

  if (Math.abs(delta) < DELTA_THRESHOLD) return;

  if (delta > 0 && !state.hidden) {
    const clearsCompensation =
      scrollY > state.headerHeight + state.hiddenOffset;
    if (clearsCompensation && hasRoomToHide(state.hiddenOffset)) {
      setHidden(state, true);
    }
  } else if (delta < 0 && state.hidden) {
    setHidden(state, false);
  }
}

function attachAutoHidingHeader(el: HTMLElement, hiddenOffset: number) {
  const state: HeaderState = {
    el,
    hiddenOffset,
    headerHeight: el.offsetHeight,
    lastScrollY: window.scrollY,
    settledAfter: 0,
    hidden: false,
  };
  let ticking = false;
  let rafId: number | null = null;

  const ro = new ResizeObserver(() => {
    state.headerHeight = el.offsetHeight;
  });
  ro.observe(el);

  const update = () => {
    ticking = false;
    updateHeader(state);
  };

  const onScroll = () => {
    if (ticking) return;
    ticking = true;
    rafId = window.requestAnimationFrame(update);
  };

  const onTransitionEnd = (event: TransitionEvent) => {
    if (event.target === el) state.settledAfter = 0;
  };

  window.addEventListener("scroll", onScroll, { passive: true });
  el.addEventListener("transitionend", onTransitionEnd);

  return () => {
    if (rafId !== null) window.cancelAnimationFrame(rafId);
    window.removeEventListener("scroll", onScroll);
    el.removeEventListener("transitionend", onTransitionEnd);
    ro.disconnect();
    el.style.transform = "";
    el.style.marginBottom = "";
  };
}

/**
 * Slides the header up by `hiddenOffset` on scroll down and back down on scroll
 * up, writing styles imperatively so scrolling never re-renders React.
 *
 * Hiding also shrinks document flow (see `setHidden`), and browsers answer
 * layout changes with scroll adjustments of their own: scroll anchoring
 * compensates for content that shifted under the viewport, and a shorter
 * document clamps the offset near its end. Those adjustments arrive as ordinary
 * scroll events pointing opposite to the toggle that caused them, so reading
 * them back as user scrolling flips the header, which adjusts the offset again,
 * and the header oscillates for as long as the page keeps moving. Three guards
 * close that loop: deltas are ignored until layout settles after a toggle, a
 * hide starts far enough below the fold that its own adjustment cannot reach
 * above it, and the header stays put when hiding it would clamp the scroll
 * offset.
 */
export function useAutoHidingHeader({
  ref,
  enabled,
  hiddenOffset,
}: AutoHidingHeaderOptions) {
  useEffect(() => {
    if (!enabled) return;

    const el = ref.current;
    if (!el) return;

    return attachAutoHidingHeader(el, hiddenOffset);
  }, [enabled, hiddenOffset, ref]);
}
